/**
 * Budget composition — THE one place user-selected effort and inferred project
 * value combine into the build's token/depth allowance:
 *
 *   tokens = baseTokensByEffort[effort] × valueTiers[tier].multiplier
 *            capped by the user's --budget override and the config hard cap.
 *
 * Depth knobs (research steps, validation passes) start from the effort profile
 * and are scaled by the same value multiplier. Everything is reported back in
 * `explanation` so the user can see — and override — exactly what was decided.
 */
import type { EffortLevel } from '@fim/futurex-protocol';
import type { PlanningConfig, ValueTier } from './planning-config.js';

export interface Budget {
  /** Output-token allowance for the whole build. */
  readonly tokens: number;
  /** Max research steps the plan may schedule. */
  readonly researchSteps: number;
  /** Tooling candidates considered per decision area. */
  readonly candidatesPerChoice: number;
  /** Verification passes scheduled during the build. */
  readonly validationPasses: number;
  /** Upper bound on plan tasks. */
  readonly maxPlanTasks: number;
  /** Human-readable derivation of the numbers above. */
  readonly explanation: string;
}

export interface BudgetOverrides {
  /**
   * User-supplied token budget (`--budget`): an explicit OVERRIDE of the
   * formula in either direction (it is the advertised recovery from budget
   * exhaustion), still bounded by the configured hard cap.
   */
  readonly maxTokens?: number;
}

const scale = (base: number, multiplier: number, min: number): number =>
  Math.max(min, Math.round(base * multiplier));

/** Compose effort (user-selected baseline) with value (inferred multiplier). */
export function computeBudget(
  effort: EffortLevel,
  valueTier: ValueTier,
  cfg: PlanningConfig,
  overrides: BudgetOverrides = {},
): Budget {
  const profile = cfg.efforts[effort];
  const multiplier = cfg.valueTiers[valueTier].multiplier;
  const base = cfg.budget.baseTokensByEffort[effort];

  const formula = Math.round(base * multiplier);
  const requested = overrides.maxTokens !== undefined && overrides.maxTokens > 0 ? overrides.maxTokens : formula;
  const tokens = Math.min(requested, cfg.budget.hardCapTokens);

  let note: string;
  if (requested !== formula) {
    note =
      tokens < requested
        ? ` — overridden by your --budget ${requested.toLocaleString()}, capped at the configured hard cap`
        : ` — overridden by your --budget`;
  } else {
    note = tokens < formula ? ' (capped by the configured hard cap)' : '';
  }

  return {
    tokens,
    researchSteps: scale(profile.researchSteps, multiplier, 1),
    candidatesPerChoice: scale(profile.candidatesPerChoice, multiplier, 1),
    validationPasses: scale(profile.validationPasses, Math.min(multiplier, 1.5), 0),
    maxPlanTasks: scale(profile.maxPlanTasks, multiplier, 2),
    explanation:
      `effort ${effort} baseline ${base.toLocaleString()} tokens × value ${valueTier} ×${multiplier}` +
      ` = ${tokens.toLocaleString()} tokens${note}`,
  };
}
